import React, {useMemo, useState} from 'react';

import {ComboBox, ComboboxItem} from '@pb/kit-component-combo-box';
import {Group} from '@pb/kit-component-group';
import {Icons} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';

import {DEFAULT_MAX_DATE, DEFAULT_MIN_DATE} from './calendar.component';
import {clampDateToRange} from './utils/clampDateToRange';
import {formatValidDateToString} from './utils/formatValidDateToString';

import type {CustomHeaderProps} from './typedef';


const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Default month/year navigation header injected into `<Calendar>` and `<CalendarCore>`.
 * Renders Prev/Next month arrow buttons and two `<ComboBox>` dropdowns — one for the
 * month and one for the year — both bounded by `minDate` / `maxDate`. When the user
 * selects a year or month, the currently selected date is clamped to the new range.
 *
 * Pass a custom component to `<Calendar>`'s `CustomHeader` prop to replace this default
 * header entirely while still receiving the same `CustomHeaderProps` shape.
 */
export const CustomCalendarHeader = (datePickerProps: CustomHeaderProps): React.JSX.Element => {
    const {
        date,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
        minDate,
        maxDate,
        changeYear,
        changeMonth,
        selectDate,
        selectedDate,
    } = datePickerProps;

    const currentYear = date.getFullYear();
    const currentMonth = date.getMonth();

    const [yearComboboxShown, setYearComboboxShown] = useState(false);
    const [monthComboboxShown, setMonthComboboxShown] = useState(false);


    // Generate year options
    const yearOptions = useMemo(() => {
        const minYear = minDate?.getFullYear() ?? 1800;
        const maxYear = maxDate?.getFullYear() ?? 2050;

        return Array.from({length: maxYear - minYear + 1}, (_, i) => maxYear - i);
    }, [minDate, maxDate]);

    // Generate month options considering min/max dates
    const monthOptions = useMemo(() => {
        const minYear = minDate?.getFullYear();
        const maxYear = maxDate?.getFullYear();
        const minMonth = minDate?.getMonth();
        const maxMonth = maxDate?.getMonth();

        const months = MONTH_NAMES.map((name, index) => ({
            value: index,
            label: name,
            disabled: false,
        }));

        // Disable months that are outside the min/max date range
        months.forEach(month => {
            month.disabled = (minYear !== undefined && currentYear === minYear && month.value < minMonth!) ||
                (maxYear !== undefined && currentYear === maxYear && month.value > maxMonth!);
        });

        return months.filter(month => !month.disabled);
    }, [minDate, maxDate, currentYear]);


    const handleYearChange = (year: number) => {
        // Update calendar view
        changeYear?.(year);

        // Update selected date - preserve month and day from selectedDate
        if (selectDate && selectedDate) {
            const newDate = new Date(selectedDate);
            newDate.setFullYear(year);
            selectDate(clampDateToRange(newDate, minDate ?? DEFAULT_MIN_DATE, maxDate ?? DEFAULT_MAX_DATE));
        }
    };

    const handleMonthChange = (month: number) => {
        // Update calendar view
        changeMonth?.(month);

        // Update selected date - preserve year and day from selectedDate
        if (selectDate && selectedDate) {
            const newDate = new Date(selectedDate);
            newDate.setMonth(month);
            selectDate(clampDateToRange(newDate, minDate ?? DEFAULT_MIN_DATE, maxDate ?? DEFAULT_MAX_DATE));
        }
    };

    // Prevent calendar from closing when clicking inside the combobox container
    const preventPropagationHandler = (event: React.MouseEvent) => {
        event.stopPropagation();
    };

    const renderDateDisplay = () => {
        return (
            <Group
                direction="h"
                alignItems="center"
                gap="s"
                onClick={preventPropagationHandler}
                onMouseDown={preventPropagationHandler}>
                <ComboBox
                    value={currentYear}
                    onChange={handleYearChange}
                    placeholder="Year"
                    itemsForScroll={6}
                    shown={yearComboboxShown}
                    style={{width: '80px'}}
                    setShown={setYearComboboxShown}>
                    {yearOptions.map(year => (
                        <ComboboxItem
                            key={year}
                            value={year}
                            caption={String(year)}>
                            {String(year)}
                        </ComboboxItem>
                    ))}
                </ComboBox>

                <ComboBox
                    value={currentMonth}
                    onChange={handleMonthChange}
                    placeholder="Month"
                    itemsForScroll={6}
                    style={{width: '100px'}}
                    shown={monthComboboxShown}
                    setShown={setMonthComboboxShown}>
                    {monthOptions.map(month => (
                        <ComboboxItem
                            key={month.value}
                            value={month.value}
                            caption={month.label}>
                            {month.label}
                        </ComboboxItem>
                    ))}
                </ComboBox>
            </Group>
        );
    };

    return (
        <>
            <Group
                className="react-datepicker__custom-header"
                direction="h"
                justify="space-between"
                alignItems="center">
                <BasicIconButton
                    aria-label="Previous month"
                    className="react-datepicker__custom-header-arrow_left"
                    iconProps={{type: Icons.CHEVRON_LEFT, size: 'Size_14'}}
                    onClick={event => {
                    // Ensures proper focus handling across browsers when navigating months.
                        event.currentTarget.focus();
                        decreaseMonth();
                    }}
                    disabled={prevMonthButtonDisabled}
                />

                <strong>
                    {formatValidDateToString(date, 'MMMM yyyy')}
                </strong>

                <BasicIconButton
                    aria-label="Next month"
                    className="react-datepicker__custom-header-arrow_right"
                    iconProps={{type: Icons.CHEVRON_RIGHT, size: 'Size_14'}}
                    onClick={increaseMonth}
                    disabled={nextMonthButtonDisabled}
                />
            </Group>

            {renderDateDisplay()}
        </>
    );
};
